import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import SavePlanDialog from "@/components/SavePlanDialog";
import { Barbell, Trash, PencilSimple, Lightning, CheckCircle } from "@phosphor-icons/react";

export default function WorkoutLogCard() {
  const [logs, setLogs] = useState(null);
  const [edit, setEdit] = useState(null);

  const load = () => api.get("/workouts?days=30").then((r) => setLogs(r.data)).catch(() => setLogs([]));
  useEffect(() => { load(); }, []);

  const toggle = (w, idx) => {
    setLogs((ls) => ls.map((l) => l.id === w.id
      ? { ...l, items: l.items.map((it, i) => (i === idx ? { ...it, done: !it.done } : it)) } : l));
    api.post(`/workouts/${w.id}/toggle/${idx}`).catch(() => load());
  };

  const remove = async (id) => {
    await api.delete(`/workouts/${id}`);
    toast.success("Тренировка удалена");
    load();
  };

  return (
    <div className="glass-card p-4 sm:p-5" data-testid="workout-log-card">
      <div className="flex items-center gap-2 mb-1">
        <Barbell size={18} weight="duotone" className="text-[#00c9a7]" />
        <h3 className="font-heading font-semibold">Мои тренировки и упражнения</h3>
      </div>
      <p className="text-[11px] text-muted-foreground mb-3">
        Планы из чата с ассистентом. Отмечайте выполненные упражнения — Whoop-активность в тот же день подтянется автоматически.
      </p>
      {logs === null ? (
        <div className="h-16 rounded-lg bg-secondary/40 animate-pulse" />
      ) : logs.length === 0 ? (
        <p className="text-sm text-muted-foreground" data-testid="workout-log-empty">
          Пока пусто. Попросите ассистента составить тренировку и нажмите «Сохранить план» под его ответом.
        </p>
      ) : (
        <div className="space-y-3">
          {logs.map((w) => {
            const done = w.items.filter((i) => i.done).length;
            const complete = done === w.items.length;
            return (
              <div key={w.id} className={`rounded-xl border p-3 transition-colors ${complete ? "border-[#00e676]/40 bg-[#00e676]/[0.05]" : "border-border/60"}`} data-testid={`workout-${w.id}`}>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-semibold">{w.title}</span>
                  {complete && <CheckCircle size={15} weight="fill" className="text-[#00e676]" />}
                  <span className="text-[11px] text-muted-foreground num">
                    {new Date(w.date + "T00:00:00").toLocaleDateString("ru-RU", { day: "numeric", month: "short" })}{w.time ? ` · ${w.time}` : ""}
                  </span>
                  <span className="text-[11px] num text-muted-foreground ml-auto">{done}/{w.items.length}</span>
                  <button onClick={() => setEdit(w)} data-testid={`workout-edit-${w.id}`} className="p-1 text-muted-foreground hover:text-primary transition-colors"><PencilSimple size={14} /></button>
                  <button onClick={() => remove(w.id)} data-testid={`workout-delete-${w.id}`} className="p-1 text-muted-foreground hover:text-destructive transition-colors"><Trash size={14} /></button>
                </div>
                <div className="h-1 rounded-full bg-secondary mt-1.5 mb-2 overflow-hidden">
                  <div className="h-full rounded-full bg-[#00c9a7] transition-all" style={{ width: `${(done / w.items.length) * 100}%` }} />
                </div>
                <div className="space-y-1">
                  {w.items.map((it, i) => (
                    <button key={i} onClick={() => toggle(w, i)} data-testid={`workout-item-${w.id}-${i}`}
                      className="w-full flex items-center gap-2 text-left px-1.5 py-1 rounded-md hover:bg-secondary/40 transition-colors">
                      <span className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 transition-colors ${it.done ? "bg-[#00c9a7] border-[#00c9a7]" : "border-border"}`}>
                        {it.done && <CheckCircle size={12} weight="fill" className="text-white" />}
                      </span>
                      <span className={`text-xs ${it.done ? "line-through text-muted-foreground" : ""}`}>{it.name}</span>
                      {it.detail && <span className="text-[10px] text-muted-foreground num ml-auto shrink-0">{it.detail}</span>}
                    </button>
                  ))}
                </div>
                {w.whoop && (
                  <div className="flex items-center gap-1.5 flex-wrap mt-2 text-[11px] rounded-lg bg-[#0084ff]/10 border border-[#0084ff]/25 px-2.5 py-1.5" data-testid={`workout-whoop-${w.id}`}>
                    <Lightning size={12} weight="fill" className="text-[#0084ff]" />
                    <span className="font-semibold text-[#0084ff]">Whoop:</span>
                    <span>{w.whoop.sport}</span>
                    {w.whoop.duration_min != null && <span className="num">· {w.whoop.duration_min} мин</span>}
                    {w.whoop.kcal != null && <span className="num">· {w.whoop.kcal} ккал</span>}
                    {w.whoop.strain != null && <span className="num">· нагрузка {w.whoop.strain}</span>}
                    {w.whoop.avg_hr != null && <span className="num">· пульс {w.whoop.avg_hr}</span>}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
      <SavePlanDialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}
        plan={edit ? { kind: "workout", ...edit } : null} editId={edit?.id} onSaved={load} />
    </div>
  );
}
